import { useCallback, useEffect, useReducer } from 'react'

import { useSearchParams } from 'react-router-dom'

import { AuditLogList } from '@tribeplatform/react-components/AuditLog'
import { PlanBadge } from '@tribeplatform/react-components/common/components'
import { Form } from '@tribeplatform/react-components/Form'
import { T, useI18n, useStaticIntl } from '@tribeplatform/react-components/i18n'
import { useNetwork } from '@tribeplatform/react-sdk/hooks'
import { Card } from '@tribeplatform/react-ui-kit/Card'
import { Container, GridList } from '@tribeplatform/react-ui-kit/Layout'

import { auditLogsActionTypesFactory } from './constants.js'

type QueryState = {
  filters: {
    spaceId?: string
    memberId?: string
    noun?: string
  }
  page: number
}

type FilterAction =
  | { type: 'SET_SPACE_ID'; payload: string | undefined }
  | { type: 'SET_MEMBER_ID'; payload: string | undefined }
  | { type: 'SET_NOUN'; payload: string | undefined }
  | { type: 'SET_PAGE'; payload: number }
  | { type: 'RESET_PAGE' }

const filtersReducer = (
  state: QueryState,
  action: FilterAction,
): QueryState => {
  switch (action.type) {
    case 'SET_SPACE_ID':
      return {
        ...state,
        filters: { ...state.filters, spaceId: action.payload },
        page: 1,
      }
    case 'SET_MEMBER_ID':
      return {
        ...state,
        filters: { ...state.filters, memberId: action.payload },
        page: 1,
      }
    case 'SET_NOUN':
      return {
        ...state,
        filters: { ...state.filters, noun: action.payload || '' },
        page: 1,
      }
    case 'SET_PAGE':
      return { ...state, page: action.payload }
    case 'RESET_PAGE':
      return { ...state, page: 1 }
    default:
      return state
  }
}

function createInitialState(search: URLSearchParams): QueryState {
  const pageParam = parseInt(search.get('page') || '1', 10)
  return {
    filters: {
      spaceId: search.get('spaceId') || undefined,
      memberId: search.get('memberId') || undefined,
      noun: search.get('noun') || '',
    },
    page: !Number.isFinite(pageParam) || pageParam < 1 ? 1 : pageParam,
  }
}

export const AuditLogs = () => {
  const { $t } = useI18n()
  const actionTypeOptions = useStaticIntl(auditLogsActionTypesFactory)
  const [searchParams, setSearchParams] = useSearchParams()

  const [query, dispatch] = useReducer(
    filtersReducer,
    searchParams,
    createInitialState,
  )

  // Keep URL search params in sync with state changes
  useEffect(() => {
    const params: Record<string, string> = {}
    const { filters, page } = query
    if (filters.noun) {
      params.noun = filters.noun
    }
    if (filters.spaceId) {
      params.spaceId = filters.spaceId
    }
    if (filters.memberId) {
      params.memberId = filters.memberId
    }
    if (page && page !== 1) {
      params.page = String(page)
    }
    setSearchParams(params, { preventScrollReset: true })
  }, [query, setSearchParams])

  // Handlers (memoized)
  const handleSetSpaceId = useCallback(
    (ids: string[]) => {
      dispatch({ type: 'SET_SPACE_ID', payload: ids?.at(0) })
    },
    [dispatch],
  )

  const handleSetMemberId = useCallback(
    (ids: string[]) => {
      dispatch({ type: 'SET_MEMBER_ID', payload: ids?.at(0) })
    },
    [dispatch],
  )

  const handleSetNoun = useCallback(
    (noun?: string) => {
      dispatch({ type: 'SET_NOUN', payload: noun })
    },
    [dispatch],
  )

  const handlePageChange = useCallback(
    (page: number) => {
      dispatch({ type: 'SET_PAGE', payload: page })
    },
    [dispatch],
  )

  const { data: network } = useNetwork({
    fields: {
      limits: 'all',
    },
  })
  const authorized = !!network?.limits?.usageLimits.auditLogAccess
  const { filters, page } = query
  return (
    <Container size="lg">
      <Container
        size="lg"
        padding={{ horizontal: { size: 'md', onlyOnMobile: true } }}
      >
        <div className="flex items-center space-s-2">
          <h3 className="text-heading-sm font-medium">
            <T defaultMessage="Audit logs" id="Generics.AuditLogs" />
          </h3>
          <PlanBadge authorized={authorized} />
        </div>
        <Form
          onSubmit={null}
          defaultValues={{
            timeframe: '168',
            memberId: filters.memberId ? [filters.memberId] : [],
            spaceId: filters.spaceId ? [filters.spaceId] : [],
            noun: filters.noun,
          }}
        >
          <GridList columns={3} className="mb-5">
            <GridList.Item>
              <Form.MemberIdPicker
                multiple={false}
                name="memberId"
                onChange={handleSetMemberId}
                label={$t({
                  defaultMessage: '{MEMBER_CC}',
                  id: 'Generics.Member',
                })}
                placeholder={$t({
                  defaultMessage: 'Select a {MEMBER}...',
                  id: 'Admin.AuditLogs.Filters.Actor.Placeholder',
                })}
              />
            </GridList.Item>
            <GridList.Item>
              <Form.SpaceIdPicker
                multiple={false}
                name="spaceId"
                onChange={handleSetSpaceId}
                label={$t({
                  defaultMessage: '{SPACE_CC}',
                  id: 'Generics.Space',
                })}
                placeholder={$t({
                  defaultMessage: 'Select a {SPACE}...',
                  id: 'Generics.SelectSpace',
                })}
              />
            </GridList.Item>
            <Form.Select
              name="noun"
              label={$t({
                defaultMessage: 'Action type',
                id: 'Admin.AuditLogs.Filters.ActionType',
              })}
              value={filters.noun}
              onChange={handleSetNoun}
              items={actionTypeOptions}
            />
          </GridList>
        </Form>
      </Container>
      <Card>
        <Card.Content>
          <AuditLogList
            authorized={authorized}
            filters={filters}
            page={page}
            onPageChange={handlePageChange}
          />
        </Card.Content>
      </Card>
    </Container>
  )
}
